import type {
  Drive, Folder, Permission, Responsible, Rights, Server, Share,
} from '../../types.ts'
import { presetByValue, rightsToPreset } from '../../types.ts'

/**
 * Farb-Codierung anhand der Preset-Kategorie — bildet die NTFS-Rechte
 * auf visuelle Klassen ab:
 *   Gruen  = Bearbeiten / Vollzugriff (Modify, FullControl)
 *   Amber = Einwurf-Postfach (Write ohne Read) — klare Mischfarbe
 *   Gelb  = Nur-Lesen (Read, ReadExecute, ListFolder)
 *   Rot   = DENY (None) — explizite Verweigerung
 *   Grau  = Entzogen (Revoke) — kein Eintrag in der ACL, kein DENY
 *   Weiss  = keine Berechtigung gesetzt (erbt vom Parent)
 *
 * Bei `inherited === true` wird eine blassere Variante gezeigt, damit direkt
 * gesetzte Rechte optisch hervorstechen.
 */
export function rightsBgClass(rights: Rights | null, inherited: boolean): string {
  if (!rights) return 'bg-white'
  const preset = rightsToPreset(rights)
  const cat = presetByValue(preset).category
  if (cat === 'white') return 'bg-white'
  if (inherited) {
    if (cat === 'green') return 'bg-green-50 text-green-700'
    if (cat === 'mixed') return 'bg-amber-50 text-amber-700'
    if (cat === 'yellow') return 'bg-yellow-50 text-yellow-800'
    if (cat === 'gray') return 'bg-gray-50 text-gray-500'
    return 'bg-red-50 text-red-600'
  }
  if (cat === 'green') return 'bg-green-300 text-green-900'
  if (cat === 'mixed') return 'bg-amber-200 text-amber-900'
  if (cat === 'yellow') return 'bg-yellow-300 text-yellow-900'
  if (cat === 'gray') return 'bg-gray-200 text-gray-700'
  return 'bg-red-200 text-red-800'
}

/**
 * Normalisiert einen Freitext-Namen (z.B. "Fuehrungsebene") zu einem
 * AD-Gruppennamen ohne Umlaute/Sonderzeichen (z.B. "Fuehrungsebene").
 */
export function toAdName(raw: string): string {
  return raw
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
    .replace(/[^a-zA-Z0-9]+/g, '')
}

/**
 * Vererbungs-Resolution fuer Verantwortliche: gibt die erste nicht-leere
 * `responsibleEmployees`-Liste entlang der Parent-Kette zurueck (Folder →
 * Parent → … → Share-Root). Nur wenn der Ordner selbst keine eigenen
 * Verantwortlichen hat.
 *
 * Gibt zusaetzlich `sourceLabel` zurueck fuer die Tooltip-Anzeige
 * ("Geerbt von Ordner XY" / "Geerbt von Share-Root").
 */
export function getInheritedResponsibles(
  folder: Folder,
  allFolders: Folder[],
  allShares: Share[],
): { list: Responsible[]; sourceLabel: string } | null {
  if (folder.responsibleEmployees && folder.responsibleEmployees.length > 0) return null

  let cur: Folder | undefined = folder.parent
    ? allFolders.find((x) => x.id === folder.parent)
    : undefined
  while (cur) {
    if (cur.responsibleEmployees && cur.responsibleEmployees.length > 0) {
      return { list: cur.responsibleEmployees, sourceLabel: `Geerbt von Ordner „${cur.name}"` }
    }
    cur = cur.parent ? allFolders.find((x) => x.id === cur!.parent) : undefined
  }

  const share = allShares.find((s) => s.id === folder.shareId)
  if (share?.responsibleEmployees && share.responsibleEmployees.length > 0) {
    return { list: share.responsibleEmployees, sourceLabel: `Geerbt von Share „${share.name}"` }
  }
  return null
}

/** Folder-Tiefe (0 = Top-Level, 1 = Ebene 2, 2+ = Sub-Sub-…). */
export function folderDepth(folder: Folder, allFolders: Folder[]): number {
  let d = 0
  let cur: Folder | undefined = folder
  while (cur?.parent) {
    const p = allFolders.find((x) => x.id === cur!.parent)
    if (!p) break
    cur = p
    d++
  }
  return d
}

/**
 * Ermittelt den effektiven Drive eines Ordners — direkt via f.driveId
 * oder geerbt vom ersten Vorfahren, der einen hat.
 */
export function resolveDrive(
  folder: Folder,
  allFolders: Folder[],
  allDrives: Drive[],
): Drive | null {
  const direct = folder.driveId
    ? allDrives.find((d) => d.id === folder.driveId)
    : null
  if (direct) return direct
  if (folder.parent) {
    const parent = allFolders.find((x) => x.id === folder.parent)
    if (parent) return resolveDrive(parent, allFolders, allDrives)
  }
  return null
}

/** Sortiert Folders in depth-first / parent-before-child Reihenfolge. */
export function orderHierarchically(list: Folder[]): Folder[] {
  const result: Folder[] = []
  const byParent = new Map<string | null, Folder[]>()
  for (const f of list) {
    const key = f.parent ?? null
    const arr = byParent.get(key) ?? []
    arr.push(f)
    byParent.set(key, arr)
  }
  function emit(parentId: string | null) {
    const children = (byParent.get(parentId) ?? []).sort((a, b) =>
      a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }),
    )
    for (const c of children) {
      result.push(c)
      emit(c.id)
    }
  }
  // Root = erste Ebene innerhalb dieses Folder-Sets (parent = null oder
  // parent zeigt auf einen Folder, der nicht zum Set gehoert)
  const ids = new Set(list.map((f) => f.id))
  const rootIds: (string | null)[] = []
  const seenRoots = new Set<string | null>()
  for (const f of list) {
    const rootKey = f.parent && ids.has(f.parent) ? null : (f.parent ?? null)
    if (!seenRoots.has(rootKey)) {
      seenRoots.add(rootKey)
      rootIds.push(rootKey)
    }
  }
  for (const r of rootIds) emit(r)
  return result
}

/**
 * Baut die flache Zeilen-Reihenfolge fuer die Excel-artige Matrix auf:
 * pro Drive einen Section-Header gefolgt von allen Foldern in
 * Hierarchie-Reihenfolge (depth-first). Folders ohne Drive landen am
 * Ende in einer "Ohne Laufwerk"-Sektion.
 */
export function buildDriveGroups(
  selectedServer: Server | undefined,
  allFolders: Folder[],
  allDrives: Drive[],
): Array<{ drive: Drive | null; folders: Folder[] }> {
  if (!selectedServer) return []
  const shareIds = new Set(selectedServer.shares.map((sh) => sh.id))
  const relevant = allFolders.filter((f) => shareIds.has(f.shareId))
  const byDrive = new Map<string, Folder[]>()
  const orphans: Folder[] = []
  for (const f of relevant) {
    const drv = resolveDrive(f, allFolders, allDrives)
    if (!drv) {
      orphans.push(f)
      continue
    }
    const list = byDrive.get(drv.id) ?? []
    list.push(f)
    byDrive.set(drv.id, list)
  }
  const drivesSorted = [...allDrives].sort((a, b) =>
    (a.letter || 'Z').localeCompare(b.letter || 'Z'),
  )
  const groups: Array<{ drive: Drive | null; folders: Folder[] }> = []
  for (const drv of drivesSorted) {
    const list = byDrive.get(drv.id)
    if (!list || list.length === 0) {
      groups.push({ drive: drv, folders: [] })
      continue
    }
    groups.push({ drive: drv, folders: orderHierarchically(list) })
  }
  if (orphans.length > 0) {
    groups.push({ drive: null, folders: orderHierarchically(orphans) })
  }
  return groups
}

export function getPermission(
  permissions: Permission[],
  folderId: string,
  groupId: string,
): Rights | null {
  return permissions.find((p) => p.folderId === folderId && p.groupId === groupId)?.rights ?? null
}

/**
 * Wie `getPermission`, gibt aber den kompletten Permission-Eintrag zurueck —
 * inklusive `noInherit`. Wird fuer die Vererbungs-Resolution benoetigt, damit
 * ein „Nur dieser Ordner"-ACE nicht faelschlich an Kinder weitergegeben wird.
 */
export function getPermissionEntry(
  permissions: Permission[],
  folderId: string,
  groupId: string,
): Permission | null {
  return permissions.find((p) => p.folderId === folderId && p.groupId === groupId) ?? null
}

/**
 * Direkt auf dem Share-Root gesetzte Berechtigung (kein Folder dazwischen).
 */
export function getSharePermission(
  permissions: Permission[],
  shareId: string,
  groupId: string,
): Rights | null {
  return permissions.find((p) => !p.folderId && p.shareId === shareId && p.groupId === groupId)?.rights ?? null
}

export function getSharePermissionEntry(
  permissions: Permission[],
  shareId: string,
  groupId: string,
): Permission | null {
  return permissions.find((p) => !p.folderId && p.shareId === shareId && p.groupId === groupId) ?? null
}

/**
 * Vererbungskette: Folder → Parent → … → Share-Root. Gibt das erste
 * explizit gesetzte Recht in der Kette zurueck, das auch tatsaechlich vererbt —
 * Eintraege mit `noInherit: true` (NTFS-Scope „Nur dieser Ordner") werden
 * beim Hochlaufen uebersprungen. Ohne diesen Skip wuerde z.B. ein
 * Traversal-Recht am Share-Root automatisch an alle Geschwister-Ordner
 * gelangen und dort unerwuenschten Zugriff erzeugen.
 */
export function getInheritedRights(
  folderId: string,
  groupId: string,
  allFolders: Folder[],
  allPermissions: Permission[],
): Rights | null {
  const folder = allFolders.find((f) => f.id === folderId)
  if (!folder) return null
  if (folder.parent) {
    const entry = getPermissionEntry(allPermissions, folder.parent, groupId)
    if (entry && !entry.noInherit) return entry.rights
    return getInheritedRights(folder.parent, groupId, allFolders, allPermissions)
  }
  // Top-Level-Folder erbt vom Share-Root — aber nur, wenn der Eintrag vererbt.
  const shareEntry = getSharePermissionEntry(allPermissions, folder.shareId, groupId)
  if (shareEntry && !shareEntry.noInherit) return shareEntry.rights
  return null
}

/**
 * Formatiert Verantwortlichen-Felder fuer die Anzeige in einem Text-Input.
 * Format: "Hauptverantwortlicher (Vertreter)" oder nur "Hauptverantwortlicher".
 */
export function formatResponsibleDisplay(
  responsibleName?: string,
  deputyName?: string,
): string {
  if (!responsibleName) return ''
  if (deputyName) return `${responsibleName} (${deputyName})`
  return responsibleName
}

/**
 * Parst einen Verantwortlichen-Text im Format "Haupt (Vertretung)" zurueck
 * in strukturierte Felder.
 */
export function parseResponsibleText(text: string): {
  responsibleName: string
  deputyName: string
} {
  const match = /^(.+?)\s*\((.+)\)\s*$/.exec(text.trim())
  if (match) {
    return { responsibleName: match[1]!.trim(), deputyName: match[2]!.trim() }
  }
  return { responsibleName: text.trim(), deputyName: '' }
}
